import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Send, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = '';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Message envoyé avec succès!');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        toast.error('Erreur lors de l\'envoi du message');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FCFBF9]" data-testid="contact-page">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <nav className="breadcrumb flex items-center gap-2 text-sm">
          <Link to="/" className="hover:text-[#1A1514]">Accueil</Link>
          <span>/</span>
          <span className="text-[#1A1514]">Contact</span>
        </nav>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="font-serif text-4xl mb-4">Contacte-nous</h1>
          <p className="text-[#6B6A68] max-w-2xl mx-auto">
            Une question? Un conseil? Notre équipe est là pour t'aider.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-white p-8">
            <h2 className="font-serif text-2xl mb-6">Envoie-nous un message</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-[#6B6A68] block mb-2">Nom *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="checkout-input"
                    data-testid="contact-name"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm text-[#6B6A68] block mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="checkout-input"
                    data-testid="contact-email"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm text-[#6B6A68] block mb-2">Téléphone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="checkout-input"
                  data-testid="contact-phone"
                />
              </div>

              <div>
                <label className="text-sm text-[#6B6A68] block mb-2">Sujet *</label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="checkout-input"
                  data-testid="contact-subject"
                  required
                />
              </div>

              <div>
                <label className="text-sm text-[#6B6A68] block mb-2">Message *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={5}
                  className="checkout-input resize-none"
                  data-testid="contact-message"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full py-4 text-sm font-medium uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="contact-submit-btn"
              >
                {loading ? 'Envoi...' : (
                  <>
                    <Send size={18} />
                    Envoyer le message
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="space-y-8">
            {/* WhatsApp */}
            <div className="bg-[#25D366] p-8 text-white">
              <div className="flex items-center gap-4 mb-4">
                <MessageCircle size={32} />
                <h2 className="font-serif text-2xl">WhatsApp</h2>
              </div>
              <p className="mb-6 opacity-90">
                Le moyen le plus rapide de nous contacter. Réponse garantie en moins de 24h.
              </p>
              <a 
                href="https://wa.me/213782367184?text=Bonjour Bloom Lingerie!"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-white text-[#25D366] px-6 py-3 font-medium"
                data-testid="whatsapp-contact-btn"
              >
                Ouvrir WhatsApp
              </a>
            </div>

            {/* Info Cards */}
            <div className="bg-white p-8 space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#F4E3E1] rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin size={20} className="text-[#D4AF37]" />
                </div>
                <div>
                  <h3 className="font-medium mb-1">Adresse</h3>
                  <p className="text-sm text-[#6B6A68]">
                    Bazar Hamza Bachdjerrah No164<br />
                    Alger, Algérie
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#F4E3E1] rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone size={20} className="text-[#D4AF37]" />
                </div>
                <div>
                  <h3 className="font-medium mb-1">Téléphone</h3>
                  <a href="tel:+213782367184" className="text-sm text-[#6B6A68] hover:text-[#D4AF37]">
                    +213 782 36 71 84
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#F4E3E1] rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail size={20} className="text-[#D4AF37]" />
                </div>
                <div>
                  <h3 className="font-medium mb-1">Email</h3>
                  <a href="mailto:bloom.lingerie.store@gmail.com" className="text-sm text-[#6B6A68] hover:text-[#D4AF37]">
                    bloom.lingerie.store@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#F4E3E1] rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock size={20} className="text-[#D4AF37]" />
                </div>
                <div>
                  <h3 className="font-medium mb-1">Horaires</h3>
                  <p className="text-sm text-[#6B6A68]">
                    Samedi - Jeudi: 9h30 - 18h00<br />
                    Vendredi: Fermé
                  </p>
                </div>
              </div>
            </div>

            {/* Shipping Info */}
            <div className="bg-[#F4E3E1] p-8">
              <h3 className="font-serif text-xl mb-4">Livraison</h3>
              <p className="text-sm text-[#6B6A68] mb-4">
                Nous livrons dans les 58 wilayas d'Algérie via World Express.
              </p>
              <ul className="text-sm text-[#6B6A68] space-y-2">
                <li>• Livraison gratuite à partir de 10 000 DA</li>
                <li>• Paiement à la livraison disponible</li>
                <li>• Échange permis pendant 48h</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
